import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";
import getApiBase from "../../../components/getApiBase";
import { adminV2Theme as t } from "../theme";

function HubTile({ title, lines, status, statusColor, to, soon }) {
  const clickable = !soon && to;
  const sx = {
    textAlign: "left",
    cursor: clickable ? "pointer" : "default",
    background: t.colors.surface,
    border: `1px solid ${t.colors.border}`,
    borderRadius: `${t.radius.md}px`,
    boxShadow: t.shadow.card,
    p: 2,
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
    gap: 0.75,
    transition: "border-color 0.15s, transform 0.15s",
    fontFamily: t.font.family,
    opacity: soon ? 0.65 : 1,
    textDecoration: "none",
    color: "inherit",
    "&:hover": clickable
      ? { borderColor: t.colors.primary, transform: "translateY(-1px)" }
      : {},
  };

  const body = (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: t.colors.text }}>{title}</Typography>
        <Chip
          label={status}
          size="small"
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            background: statusColor || t.colors.primarySoft,
            color: t.colors.text,
          }}
        />
      </Box>
      {lines.map((line) => (
        <Typography key={line} sx={{ fontSize: 13, color: t.colors.textMuted }}>
          {line}
        </Typography>
      ))}
      {clickable && (
        <Typography sx={{ fontSize: 12, color: t.colors.primary, fontWeight: 600, mt: "auto" }}>
          Open →
        </Typography>
      )}
    </>
  );

  if (to && !soon) {
    return (
      <Box component={Link} to={to} sx={sx}>
        {body}
      </Box>
    );
  }
  return (
    <Box component="div" sx={sx}>
      {body}
    </Box>
  );
}

export default function DevHubPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupCount, setGroupCount] = useState(null);
  const [seedMsg, setSeedMsg] = useState("");
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/groups`, {
        credentials: "include",
      });
      if (res.status === 403) {
        setError("You do not have vault or Development hub access yet.");
        setGroupCount(0);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (${res.status})`);
      }
      const data = await res.json();
      setGroupCount((data.groups || []).length);
    } catch (e) {
      setError(e.message || "Failed to load Development hub");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const seedDatabasePackage = async () => {
    setSeeding(true);
    setSeedMsg("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/seed/database-package`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Seed failed (${res.status})`);
      setSeedMsg(`Database package ready (${(body.entry_ids || []).length} entries).`);
    } catch (e) {
      setSeedMsg(e.message || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: t.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
        Development & Health
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, mb: 2.5, fontSize: 14 }}>
        Platform vault, tool access requests, and developer how-tos. Hub tiles open tools — no long scroll pages.
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {seedMsg && (
        <Alert severity={seedMsg.includes("failed") || seedMsg.includes("Failed") ? "error" : "success"} sx={{ mb: 2 }}>
          {seedMsg}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 2,
          mb: 2,
        }}
      >
        <HubTile
          title="Platform vault"
          status="Live"
          statusColor={t.colors.successSoft}
          lines={[
            groupCount != null ? `${groupCount} sub-vaults` : "Secrets, schemas, how-tos",
            "Lobby, dossier search, entries",
          ]}
          to="/admin/v2/dev/vault"
        />
        <HubTile
          title="Tool access requests"
          status="Live"
          statusColor={t.colors.successSoft}
          lines={[
            "Request personal access to Supabase, Vercel, Railway, Cloudflare…",
            "Elevated approvers invite your account — no shared master password",
          ]}
          to="/admin/v2/dev/access-requests"
        />
        <HubTile
          title="Health & CI"
          status="Soon"
          statusColor={t.colors.accentSoft}
          lines={["Preview health links", "Pipeline pointers"]}
          soon
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <Button
          variant="outlined"
          size="small"
          disabled={seeding}
          onClick={seedDatabasePackage}
          sx={{ textTransform: "none", borderColor: t.colors.border, color: t.colors.text }}
        >
          {seeding ? "Seeding…" : "Seed Database package"}
        </Button>
        <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
          Idempotent schema/howto/policy stubs into vault (needs vault:admin)
        </Typography>
      </Box>
    </Box>
  );
}

export function VaultGroupTile({ group, onOpen }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(group.key)}
      sx={{
        textAlign: "left",
        cursor: "pointer",
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        borderRadius: `${t.radius.md}px`,
        boxShadow: t.shadow.card,
        p: 2,
        minHeight: 110,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        fontFamily: t.font.family,
        transition: "border-color 0.15s, transform 0.15s",
        "&:hover": { borderColor: t.colors.primary, transform: "translateY(-1px)" },
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: t.colors.text }}>
        {group.display_name}
      </Typography>
      <Typography sx={{ fontSize: 13, color: t.colors.textMuted, flex: 1 }}>
        {group.description || "—"}
      </Typography>
      <Typography sx={{ fontSize: 12, color: t.colors.primary, fontWeight: 600 }}>Open →</Typography>
    </Box>
  );
}

export function VaultSearchShell({
  placeholder,
  disabled,
  value,
  onChange,
  onSubmit,
  helperText,
}) {
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (onSubmit) onSubmit();
      }}
      sx={{ mb: 2, maxWidth: 560, display: "flex", gap: 1 }}
    >
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        helperText={helperText}
        sx={{ "& .MuiOutlinedInput-root": { background: t.colors.surface } }}
      />
      {onSubmit && (
        <Button
          type="submit"
          variant="contained"
          disabled={disabled}
          sx={{
            textTransform: "none",
            background: t.colors.primary,
            fontWeight: 600,
            height: 40,
            "&:hover": { background: t.colors.primaryHover },
          }}
        >
          Search
        </Button>
      )}
    </Box>
  );
}

export function AccessRequestsPage() {
  const TOOL_OPTIONS = [
    { value: "supabase", label: "Supabase" },
    { value: "vercel", label: "Vercel" },
    { value: "railway", label: "Railway" },
    { value: "cloudflare", label: "Cloudflare" },
    { value: "github", label: "GitHub" },
    { value: "other", label: "Other (describe in justification)" },
  ];
  const ENV_OPTIONS = [
    { value: "", label: "N/A (account-wide)" },
    { value: "preview", label: "Preview" },
    { value: "preprod", label: "Preprod" },
    { value: "production", label: "Production" },
    { value: "org", label: "Org / account" },
    { value: "all", label: "All environments" },
  ];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [form, setForm] = useState({
    resource_type: "supabase",
    environment: "",
    justification: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/access-requests`, {
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Failed (${res.status})`);
      setRequests(body.requests || []);
      setCanManage(Boolean(body.can_manage));
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/access-requests`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resource_type: form.resource_type,
          environment: form.environment || null,
          justification: form.justification,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Submit failed");
      setForm((f) => ({ ...f, justification: "" }));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const decide = async (id, status) => {
    const notes =
      status === "approved"
        ? "Invite sent (or to be sent) to requester personal email — no master password shared."
        : "";
    const res = await fetch(`${getApiBase()}/api/admin/v2/vault/access-requests/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, notes }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(body.error || "Update failed");
      return;
    }
    await load();
    if (body.reminder) {
      window.alert(body.reminder);
    }
  };

  const toolLabel = (key) => {
    const hit = TOOL_OPTIONS.find((o) => o.value === key);
    if (hit) return hit.label;
    if (key === "supabase_db") return "Supabase";
    return key || "—";
  };

  const envLabel = (env) => {
    if (!env || env === "n_a") return "N/A";
    return env;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: t.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
        Tool access requests
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, mb: 2, fontSize: 14, maxWidth: 640 }}>
        Request personal access to a vendor tool (Supabase, Vercel, Railway, Cloudflare, GitHub…).
        Elevated approvers invite your own account — shared master passwords are never redistributed.
        This is not for vault article access (that is permission grants + entry sensitivity).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          p: 2,
          mb: 3,
          border: `1px solid ${t.colors.border}`,
          borderRadius: `${t.radius.md}px`,
          background: t.colors.surface,
          display: "grid",
          gap: 1.5,
          maxWidth: 520,
        }}
      >
        <TextField
          select
          size="small"
          label="Tool"
          required
          value={form.resource_type}
          onChange={(e) => setForm((f) => ({ ...f, resource_type: e.target.value }))}
        >
          {TOOL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Environment (optional)"
          value={form.environment}
          onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))}
          helperText="Leave N/A when the tool is account-wide"
        >
          {ENV_OPTIONS.map((opt) => (
            <MenuItem key={opt.value || "na"} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Justification"
          required
          multiline
          minRows={3}
          value={form.justification}
          onChange={(e) => setForm((f) => ({ ...f, justification: e.target.value }))}
          helperText="What you need, for how long, and which account email to invite"
        />
        <Button
          variant="contained"
          disabled={saving || !form.justification.trim() || !form.resource_type}
          onClick={submit}
          sx={{
            textTransform: "none",
            background: t.colors.primary,
            fontWeight: 600,
            alignSelf: "start",
            "&:hover": { background: t.colors.primaryHover },
          }}
        >
          {saving ? "Submitting…" : "Submit request"}
        </Button>
      </Box>

      <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Requests</Typography>
      {!canManage && (
        <Typography sx={{ fontSize: 12, color: t.colors.textMuted, mb: 1 }}>
          You see your own requests. Approve/Deny requires vault:admin or access:manage.
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {requests.length === 0 && (
          <Typography sx={{ color: t.colors.textMuted, fontSize: 13 }}>No requests yet.</Typography>
        )}
        {requests.map((r) => (
          <Box
            key={r.id}
            sx={{
              p: 1.5,
              border: `1px solid ${t.colors.border}`,
              borderRadius: `${t.radius.sm}px`,
              background: t.colors.surface,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                #{r.id} · {toolLabel(r.resource_type)} · {envLabel(r.environment)}
              </Typography>
              <Chip label={r.status} size="small" sx={{ height: 22, fontSize: 11 }} />
            </Box>
            <Typography sx={{ fontSize: 13, color: t.colors.textMuted, mb: 1 }}>
              {r.justification}
            </Typography>
            {canManage && r.status === "pending" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" sx={{ textTransform: "none" }} onClick={() => decide(r.id, "approved")}>
                  Approve
                </Button>
                <Button size="small" color="inherit" sx={{ textTransform: "none" }} onClick={() => decide(r.id, "denied")}>
                  Deny
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
